import axios from 'axios';
import { 
  League, 
  DraftPick, 
  TeamGrade, 
  ValueAnalysisResponse, 
  PositionAnalysisResponse,
  TeamDetails,
  DraftSummary,
  RosterAnalysis,
  WeeklyRosterData,
  StartSitAnalysis,
  TransactionFeed,
  SeasonOverviewData,
  LeagueDraftAnalytics,
  PlayerDetailResponse
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('draftiq_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('draftiq_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authApi = {
  login: async (username: string, password: string) => {
    const response = await api.post('/api/auth/login', { username, password });
    return response.data;
  },
  
  register: async (email: string, username: string, password: string) => {
    const response = await api.post('/api/auth/register', { email, username, password });
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('draftiq_token');
  }
};

// League endpoints - updated for real data with integer IDs
export const leagueApi = {
  getLeagues: async (): Promise<League[]> => {
    const response = await api.get('/api/leagues/');
    return response.data;
  },
  
  getLeague: async (leagueId: number): Promise<League> => {
    const response = await api.get(`/api/leagues/${leagueId}`);
    return response.data;
  },
  
  createLeague: async (leagueData: Partial<League>): Promise<League> => {
    const response = await api.post('/api/leagues/', leagueData);
    return response.data;
  },
  
  createMockLeague: async () => {
    const response = await api.post('/api/leagues/mock-league');
    return response.data;
  },
  
  getLeagueTeams: async (leagueId: number) => {
    const response = await api.get(`/api/leagues/${leagueId}/teams`);
    return response.data;
  },
  
  getLeagueDraft: async (leagueId: number): Promise<DraftPick[]> => {
    const response = await api.get(`/api/leagues/${leagueId}/draft`);
    return response.data;
  }
};

// Analytics endpoints - updated for real data structure
export const analyticsApi = {
  getLeagueDraftGrades: async (leagueId: number): Promise<TeamGrade[]> => {
    const response = await api.get(`/api/analytics/leagues/${leagueId}/draft-grades`);
    return response.data;
  },
  
  getTeamDraftGrade: async (teamId: number): Promise<TeamGrade> => {
    const response = await api.get(`/api/analytics/teams/${teamId}/draft-grade`);
    return response.data;
  },
  
  getLeagueValueAnalysis: async (leagueId: number): Promise<ValueAnalysisResponse> => {
    const response = await api.get(`/api/analytics/leagues/${leagueId}/value-analysis`);
    return response.data;
  },
  
  getPositionAnalysis: async (leagueId: number): Promise<PositionAnalysisResponse> => {
    const response = await api.get(`/api/analytics/leagues/${leagueId}/position-analysis`);
    return response.data.position_analysis;
  },

  getTeamRosterAnalysis: async (teamId: number): Promise<RosterAnalysis> => {
    const response = await api.get(`/api/analytics/teams/${teamId}/roster-analysis`);
    return response.data;
  },

  getADPAnalysis: async (leagueId: number) => {
    const response = await api.get(`/api/analytics/leagues/${leagueId}/adp-analysis`);
    return response.data;
  },

  getUnrankedGems: async (leagueId: number) => {
    const response = await api.get(`/api/analytics/leagues/${leagueId}/unranked-gems`);
    return response.data;
  },

  getProjectionAnalysis: async () => {
    const response = await api.get('/api/analytics/projection-analysis');
    return response.data;
  },
  
  getSuperlatives: async (leagueId: number) => {
    const response = await api.get(`/api/v1/analytics/superlatives?league_id=${leagueId}`);
    return response.data;
  }
};

// Teams endpoints - Phase 3 additions
export const teamsApi = {
  getTeam: async (teamId: number): Promise<TeamDetails> => {
    const response = await api.get(`/api/teams/${teamId}`);
    return response.data;
  },
  
  getTeamDraftSummary: async (teamId: number): Promise<DraftSummary> => {
    const response = await api.get(`/api/teams/${teamId}/draft-summary`);
    return response.data;
  }
};

// Players endpoints - Phase 3 additions  
export const playersApi = {
  getPlayer: async (playerId: number) => {
    const response = await api.get(`/api/players/${playerId}`);
    return response.data;
  },
  
  searchPlayers: async (query: string, position?: string, limit?: number) => {
    const params = new URLSearchParams({ q: query });
    if (position) params.append('position', position);
    if (limit) params.append('limit', limit.toString());
    
    const response = await api.get(`/api/players/search?${params}`);
    return response.data;
  }
};

// V1 API endpoints - comprehensive endpoints using the new v1 API
export const v1Api = {
  // League endpoints
  leagues: {
    getAll: async () => {
      const response = await api.get('/api/v1/leagues/');
      return response.data;
    },
    getById: async (leagueId: number) => {
      const response = await api.get(`/api/v1/leagues/${leagueId}`);
      return response.data;
    },
    getByEspnId: async (espnLeagueId: string) => {
      const response = await api.get(`/api/v1/leagues/espn/${espnLeagueId}`);
      return response.data;
    },
    getTeams: async (leagueId: number) => {
      const response = await api.get(`/api/v1/leagues/${leagueId}/teams`);
      return response.data;
    },
    getTeamsByEspnId: async (espnLeagueId: string) => {
      const response = await api.get(`/api/v1/leagues/espn/${espnLeagueId}/teams`);
      return response.data;
    },
    getDraft: async (leagueId: number, roundNum?: number) => {
      const params = roundNum ? `?round_num=${roundNum}` : '';
      const response = await api.get(`/api/v1/leagues/${leagueId}/draft${params}`);
      return response.data;
    },
    getDraftByEspnId: async (espnLeagueId: string, roundNum?: number) => {
      const params = roundNum ? `?round_num=${roundNum}` : '';
      const response = await api.get(`/api/v1/leagues/espn/${espnLeagueId}/draft${params}`);
      return response.data;
    },
    getSchedule: async (leagueId: number, week?: number) => {
      const params = week ? `?week=${week}` : '';
      const response = await api.get(`/api/v1/leagues/${leagueId}/schedule${params}`);
      return response.data;
    },
    getScheduleByEspnId: async (espnLeagueId: string, week?: number) => {
      const params = week ? `?week=${week}` : '';
      const response = await api.get(`/api/v1/leagues/espn/${espnLeagueId}/schedule${params}`);
      return response.data;
    },
    getLeagueDraftAnalytics: async (leagueId: number): Promise<LeagueDraftAnalytics> => {
      const response = await api.get(`/api/v1/leagues/${leagueId}/draft-analytics`);
      return response.data;
    },
    getDraftCompleteByEspnId: async (espnLeagueId: string) => {
      const response = await api.get(`/api/v1/leagues/espn/${espnLeagueId}/draft-complete`);
      return response.data;
    }
  },

  // Team endpoints
  teams: {
    getById: async (teamId: number) => {
      const response = await api.get(`/api/v1/teams/${teamId}`);
      return response.data;
    },
    getWeeklyPerformance: async (teamId: number, weekStart = 1, weekEnd = 18) => {
      const params = new URLSearchParams({
        week_start: weekStart.toString(),
        week_end: weekEnd.toString()
      });
      const response = await api.get(`/api/v1/teams/${teamId}/weekly-performance?${params}`);
      return response.data;
    },
    getHeadToHead: async (team1Id: number, team2Id: number) => {
      const response = await api.get(`/api/v1/teams/${team1Id}/vs/${team2Id}`);
      return response.data;
    },
    getRoster: async (teamId: number, position?: string) => {
      const params = position ? `?position=${position}` : '';
      const response = await api.get(`/api/v1/teams/${teamId}/roster${params}`);
      return response.data;
    },
    
    // New Roster Page v2 endpoints
    getWeeklyRoster: async (teamId: number, week: number): Promise<WeeklyRosterData> => {
      const response = await api.get(`/api/v1/teams/${teamId}/roster/weekly/${week}`);
      return response.data;
    },
    
    getStartSitAnalysis: async (teamId: number, week: number): Promise<StartSitAnalysis> => {
      const response = await api.get(`/api/v1/teams/${teamId}/start-sit-analysis/${week}`);
      return response.data;
    },
    
    getTransactions: async (teamId: number, upToWeek?: number): Promise<TransactionFeed> => {
      const params = upToWeek ? `?up_to_week=${upToWeek}` : '';
      const response = await api.get(`/api/v1/teams/${teamId}/transactions${params}`);
      return response.data;
    },
    
    getSeasonOverview: async (teamId: number): Promise<SeasonOverviewData> => {
      const response = await api.get(`/api/v1/teams/${teamId}/season-overview`);
      return response.data;
    }
  },

  // Player endpoints
  players: {
    getAll: async (params: { position?: string; nfl_team?: string; search?: string; limit?: number; offset?: number } = {}) => {
      const searchParams = new URLSearchParams();
      if (params.position) searchParams.append('position', params.position);
      if (params.nfl_team) searchParams.append('nfl_team', params.nfl_team);
      if (params.search) searchParams.append('search', params.search);
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.offset) searchParams.append('offset', params.offset.toString());
      
      const response = await api.get(`/api/v1/players/?${searchParams}`);
      return response.data;
    },
    search: async (query: string, position?: string, limit = 20) => {
      const params = new URLSearchParams({ query, limit: limit.toString() });
      if (position) params.append('position', position);
      const response = await api.get(`/api/v1/players/search?${params}`);
      return response.data;
    },
    getById: async (playerId: number) => {
      const response = await api.get(`/api/v1/players/${playerId}`);
      return response.data;
    },
    getPlayerDetail: async (playerId: number, leagueId?: number): Promise<PlayerDetailResponse> => {
      const params = leagueId ? `?league_id=${leagueId}` : '';
      const response = await api.get(`/api/v1/players/${playerId}${params}`);
      return response.data;
    },
    getWeeklyStats: async (playerId: number, weekStart = 1, weekEnd = 18) => {
      const params = new URLSearchParams({
        week_start: weekStart.toString(),
        week_end: weekEnd.toString()
      });
      const response = await api.get(`/api/v1/players/${playerId}/weekly-stats?${params}`);
      return response.data;
    },
    getTopPerformers: async (position?: string, metric = 'total_points', limit = 20) => {
      const params = new URLSearchParams({ metric, limit: limit.toString() });
      if (position) params.append('position', position);
      const response = await api.get(`/api/v1/players/top-performers?${params}`);
      return response.data;
    },
    getByPosition: async (position: string, sortBy = 'total_points', limit = 50) => {
      const params = new URLSearchParams({ sort_by: sortBy, limit: limit.toString() });
      const response = await api.get(`/api/v1/players/by-position/${position}?${params}`);
      return response.data;
    }
  },

  // Analytics endpoints
  analytics: {
    getMatchups: async (week?: number, teamId?: number, isPlayoff?: boolean) => {
      const params = new URLSearchParams();
      if (week) params.append('week', week.toString());
      if (teamId) params.append('team_id', teamId.toString());
      if (isPlayoff !== undefined) params.append('is_playoff', isPlayoff.toString());
      
      const response = await api.get(`/api/v1/analytics/matchups?${params}`);
      return response.data;
    },
    
    getWeeklyPerformance: async (teamId?: number, playerId?: number, weekStart = 1, weekEnd = 18) => {
      const params = new URLSearchParams({
        week_start: weekStart.toString(),
        week_end: weekEnd.toString()
      });
      if (teamId) params.append('team_id', teamId.toString());
      if (playerId) params.append('player_id', playerId.toString());
      
      const response = await api.get(`/api/v1/analytics/weekly-performance?${params}`);
      return response.data;
    },
    
    getDraftAnalysis: async (leagueId?: number) => {
      const params = leagueId ? `?league_id=${leagueId}` : '';
      const response = await api.get(`/api/v1/analytics/draft-analysis${params}`);
      return response.data;
    },
    
    getStandings: async (leagueId?: number, season?: number) => {
      const params = new URLSearchParams();
      if (leagueId) params.append('league_id', leagueId.toString());
      if (season) params.append('season', season.toString());
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/api/v1/analytics/standings${queryString}`);
      return response.data;
    },
    
    getHeadToHead: async (team1Id: number, team2Id: number) => {
      const response = await api.get(`/api/v1/analytics/head-to-head/${team1Id}/${team2Id}`);
      return response.data;
    }
  }
};

export default api; 